import { Observable, Subject, Subscription, ReplaySubject, of } from 'rxjs';

import { map } from 'rxjs/operators';
import { Injectable, OnDestroy, PipeTransform } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { LocationService } from './location.service';
import { SapiService, SapiFacetResponse } from './sapi.service';
import { QueryParameter } from '../interfaces/queryString';
import { BbFacetListOption } from '../components/shared/bb-facet-list/bb-facet-list.component';
import { Router } from '@angular/router';
import { DialogDefinition, OverlayService } from './overlay.service';
import { BbFilterPreset } from 'app/components/shared/bb-filters/bb-filters.component';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';

export type FilterSet = FilterGroup[];

export interface Filter {
	id?: string;
	readOnly?: boolean;
	entity?: string;
	type: string;
	field: string;
	operator: string;
	value: string | string[];
}

export interface FilterGroup {
	filters: Filter[];
	readOnly?: boolean;
}

export interface QuickFilter {
	filters: Filter[];
}

export interface FilterConfig {
	property?: string;
	label?: string;
	type?: string;
	useFacetqueryResults?: boolean;
	entity?: string;
	pipes?: PipeTransform[];
	editing?: boolean;
	selected?: any[];
	options?: any[];
	presets?: any[];
}

@Injectable()
export class FilterSetService implements OnDestroy {

	// this filterSet variable represents a "redux like" application state
	// for the part of the component tree which provides this service (e.g. a list component)

	// it should ONLY be modified by calling methods on this service, which create a new version of this variable
	// enabling unidirectional data flow from the list component down to the underlying components
	public filterSetChangedObservable: ReplaySubject<FilterSet>;
	public filterConfigChangedObservable: Subject<FilterConfig[]>;
	public queryParamsChangedObservable: ReplaySubject<string>;
	public activeFilterChangedObservable: Subject<Filter>;
	public currentFilterObservable: Subject<string | FilterSet>;
	public savePresetObservable: Subject<any>;
	public deletePresetObservable: Subject<string>;
	protected customPresets: any[] = [];
	private filterSet: FilterSet = [];

	// baseFilterSet will be merged with FilterSets at getFilterSet() and at getFacetOptions()
	private baseFilterSet: FilterSet;
	private queryParams: HttpParams;
	private sharedContext: boolean;

	private entity: string;
	private filterConfig: FilterConfig[];
	private activeFilter: Filter;

	private subscriptions: Subscription;

	private cache: Map<string, BbFacetListOption[]>;

	private locationServiceParams: Subscription;
	private linkedToUrl: boolean;

	constructor(
		private translate: TranslateService,
		private locationService: LocationService,
		private router: Router,
		private sapiService: SapiService,
		private overlayService: OverlayService) {

		this.cache = new Map<string, BbFacetListOption[]>();

		this.filterSetChangedObservable = new ReplaySubject<FilterSet>(1);
		this.filterConfigChangedObservable = new Subject<FilterConfig[]>();
		this.activeFilterChangedObservable = new Subject<Filter>();
		this.currentFilterObservable = new Subject<string | FilterSet>();
		this.queryParamsChangedObservable = new ReplaySubject<string>(1);
		this.savePresetObservable = new Subject<any>();
		this.deletePresetObservable = new Subject<string>();

		this.queryParams = new HttpParams();
		this.sharedContext = false;

		this.subscriptions = new Subscription();
		this.subscriptions.add(
			// This is used for the quickfilters in the header on the right side
			this.filterSetChangedObservable.subscribe((filterSet: FilterSet) => {
				let changed = false;
				if (this.queryParams.get('q')) {
					this.currentFilterObservable.next('');
					changed = true;
				} else if (!filterSet || filterSet.length === 0) {
					this.currentFilterObservable.next('all');
					this.setFilterSetFromString('');
					changed = true;
				} else if (filterSet.length > 0) { // preset
					changed = this.changedCurrentFilter(filterSet);
				}
				if (!changed) {
					this.currentFilterObservable.next('');
				}
			})
		);
	}

	ngOnDestroy() {
		if (this.locationServiceParams) {
			this.locationServiceParams.unsubscribe();
		}
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	setLinkedToUrl(linked: boolean) {
		if (this.locationServiceParams) {
			this.locationServiceParams.unsubscribe();
		}
		this.linkedToUrl = linked;
		if (linked) {
			this.locationServiceParams = new Subscription();
			this.locationServiceParams.add(this.locationService.paramsChangedObservable.subscribe(params => {
				let haveFs = false;
				if (params && params.length > 0) {
					for (let i = 0; i < params.length; i++) {
						if (params[i].name === 'fs') {
							haveFs = true;
							this.setFilterSetFromString(params[i].values[0]);
						} else if (params[i].name === 'q') {
							if (params[i].values && params[i].values.length > 0) {
								this.setQuery(params[i].values[0]);
							}
						} else if (params[i].name === 'sortProperty') {
							this.locationService.setParam('sortProperty', params[i].values[0]);
						} else if (params[i].name === 'sortDirection') {
							this.locationService.setParam('sortDirection', params[i].values[0]);
						}
					}
					// We only need the q param from params
					const paramFilter = params.filter(param => param.name === 'q');
					if (!paramFilter || paramFilter.length === 0) {
						this.setQuery('');
					}
					if (!haveFs) {
						this.setFilterSetFromString('');
					}
				} else {
					this.setQuery('');
					this.setFilterSetFromString('');
				}
			}));
		}
	}

	isLinkedToUrl(): boolean {
		return this.linkedToUrl;
	}

	addQueryToFilterSet(query: string, filterSet: FilterSet): FilterSet {
		if (query && query.length > 0) {
			if (!filterSet) {
				filterSet = [];
			}
			filterSet = filterSet.filter((currentFilterGroup: FilterGroup) => {
				currentFilterGroup.filters = currentFilterGroup.filters.filter((filter: Filter) =>
					// Filter out the fulltext filter to add it again
					filter.field !== 'fulltext'
				);
				// Filter out empty filterGroups
				return currentFilterGroup.filters.length > 0;
			});

			const filterGroup: FilterGroup = { filters: [] };
			filterGroup.filters.push({
				type: this.entity,
				field: 'fulltext',
				operator: 'contains',
				value: [query.replace(/:/g, '\\:')]
			});
			filterSet.push(filterGroup);
		}
		return filterSet;
	}

	changedCurrentFilter(filterSet: FilterSet): boolean {
		let filterValue = '';
		if (filterSet.length === 1) {
			const filterGroup: FilterGroup = filterSet[0];
			if (filterGroup.filters.length === 1) {
				const filter: Filter = filterGroup.filters[0];
				if (filter.value instanceof Array) {
					if (filter.value.length === 1) {
						const singleFilterValue = filter.value[0];
						filterValue = singleFilterValue;
					}
				} else {
					filterValue = filter.value;
				}
				if (filterValue) {
					if (filterValue === 'true' || filterValue === 'false') {
						filterValue = filter.field;
					}
				}
			}
		}
		if (filterValue && filterValue.length > 0) {
			this.currentFilterObservable.next(filterValue);
			return true;
		} else {
			this.currentFilterObservable.next(filterSet);
			return true;
		}
	}

	setQuery(query: string) {
		if (!this.queryParams) {
			this.queryParams = new HttpParams();
		}

		if (query === this.queryParams.get('q')) {
			return;
		}

		if (query && query.length > 0) {
			this.queryParams = this.queryParams.delete('q');
			this.queryParams = this.queryParams.set('q', query);
			this.queryParamsChangedObservable.next(query);
			this.filterSetChangedObservable.next(this.filterSet);
		} else {
			this.queryParams = this.queryParams.delete('q');
			this.queryParamsChangedObservable.next(null);
			this.filterSetChangedObservable.next(this.filterSet);
		}
	}

	setEntity(entity: string) {
		this.entity = entity;
	}

	setSharedContext(status: boolean) {
		this.sharedContext = status;
	}

	setFilterConfig(filterConfig: FilterConfig[]) {
		this.filterConfig = filterConfig;
		this.filterConfigChangedObservable.next(filterConfig);
	}

	getFilterConfig(): FilterConfig[] {
		return this.filterConfig;
	}

	getFilterConfigForProperty(filtersetProperty: string): FilterConfig {

		if (this.filterConfig) {
			return this.filterConfig.find((filterConfig: FilterConfig) => filterConfig.property === filtersetProperty);
		}
	}

	getFilterSet(): FilterSet {
		return (this.baseFilterSet ? [...this.filterSet, ...this.baseFilterSet] : this.filterSet);
	}

	moveFilterToStart(newActiveFilter: Filter): void {
		if (this.filterSet.length) {

			// move active filter to start of its group
			// and move its group to start of the filterset
			let activeGroup: FilterGroup;

			this.filterSet.forEach((group: FilterGroup, groupIndex: number) => {
				group.filters.forEach((filter: Filter, filterIndex: number) => {
					if (filter === this.activeFilter) {
						activeGroup = group;
					}
				});
			});

			if (activeGroup) {
				activeGroup.filters.sort((x, y) => x === newActiveFilter ? -1 : y === newActiveFilter ? 0 : 1);
				this.filterSet.sort((x, y) => x === activeGroup ? -1 : y === activeGroup ? 1 : 0);
			}
		}
	}

	addFilterGroupFromFilterConfig(filterConfig: FilterConfig) {
		if (!filterConfig || !filterConfig.property) {
			return;
		}
		const newFilter: Filter = {
			type: this.entity,
			field: filterConfig.property,
			operator: 'is', // todo? if this is not enough, map filterConfig.type to a "default" operator
			value: []
		};

		const newGroup: FilterGroup = {
			filters: [newFilter]
		};

		if (!this.filterSet.length) {
			this.filterSet = [];
		}

		this.filterSet.push(newGroup);
		this.setActiveFilter(newFilter, filterConfig);
		this.moveFilterToStart(newFilter);
		this.filterSetChangedObservable.next(this.filterSet);
	}

	setFilterSet(filterSet: FilterSet) {
		this.filterSet = bbDeepCopy(filterSet);
		this.filterSetChangedObservable.next(this.filterSet);
		if (this.linkedToUrl) {
			if (this.filterSet && this.filterSet.length > 0) {
				this.locationService.setParam('fs', this.stringify(this.filterSet));
			} else {
				this.locationService.removeParam('fs');
			}
		}
	}

	setFilterSetFromString(filterSetString: string): FilterSet {
		const filterSet: FilterSet = [];

		if (filterSetString !== '') {
			const filterSetFromString = JSON.parse(filterSetString);
			if (filterSetFromString.length) {
				filterSetFromString.forEach((groupsFromString: any[]) => {

					const filterGroup: FilterGroup = { filters: [] };

					if (groupsFromString.length) {
						groupsFromString.forEach((filterFromString: any[]) => {

							const filter: Filter = {
								type: filterFromString[0],
								field: filterFromString[1],
								operator: filterFromString[2],
								value: filterFromString[3]
							};

							filterGroup.filters.push(filter);
						});

						filterSet.push(filterGroup);
					}
				});
			}
		}

		if (JSON.stringify(this.filterSet) !== JSON.stringify(filterSet)) {
			this.filterSet = filterSet;
			this.filterSetChangedObservable.next(filterSet);
			if (this.linkedToUrl) {
				if (this.filterSet && this.filterSet.length > 0) {
					this.locationService.setParam('fs', this.stringify(this.filterSet));
				} else {
					this.locationService.removeParam('fs');
				}
			}
		}

		return this.filterSet;
	}

	setActiveFilter(newActiveFilter: Filter, filterConfig?: FilterConfig) {
		this.activeFilter = newActiveFilter;
		this.activeFilterChangedObservable.next(newActiveFilter);
		this.filterSetChangedObservable.next(this.filterSet);
	}

	setBaseFilterSet(filterSet: FilterSet): FilterSetService {
		this.baseFilterSet = filterSet;
		return this;
	}

	getActiveFilter(): Filter {
		return this.activeFilter;
	}

	updateFilter(currentFilter: Filter, newFilter: Filter) {
		const active = (this.activeFilter === currentFilter);

		if (this.filterSet.length) {
			const newFilterSet: FilterSet = [... this.filterSet];

			this.filterSet.forEach((group: FilterGroup, groupIndex: number) => {
				group.filters.forEach((filter: Filter, filterIndex: number) => {
					if (filter === currentFilter) {
						newFilterSet[groupIndex].filters[filterIndex] = newFilter;
					}
				});
			});

			this.filterSet = newFilterSet;
			this.filterSetChangedObservable.next(this.filterSet);
			if (this.linkedToUrl) {
				this.locationService.setParam('fs', this.stringify(newFilterSet));
			}

			if (active) {
				this.setActiveFilter(newFilter);
			}
		}
	}

	removeFilter(filterToRemove: Filter) {
		if (this.filterSet.length) {

			const filterSet = this.filterSet.filter((currentFilterGroup: FilterGroup) => {
				currentFilterGroup.filters = currentFilterGroup.filters.filter((filter: Filter) =>
					// Filter out filterToRemove
					filter !== filterToRemove
				);
				// Filter out an empty filterGroup
				return currentFilterGroup.filters.length > 0;
			});

			if (filterSet.length === 0) {
				const queryParameters = { queryParams: {} };
				this.locationService.getParams().forEach((parameter: QueryParameter) => {
					if (parameter.name !== 'fs') {
						queryParameters.queryParams[parameter.name] = parameter.values;
					}
				});
				// Trigger url update for quickfilters, leave parameters other than fq alone
				this.router.navigate([this.router.url.split('?')[0]], queryParameters);
			}
			this.filterSetChangedObservable.next(filterSet);
		}
	}

	addFilterGroup(filterGroup: FilterGroup) {
		if (this.filterSet) {
			this.filterSet.unshift(filterGroup);
			this.filterSetChangedObservable.next(this.filterSet);
		}
	}

	stringify(filterSet: FilterSet): string {

		const filterForUrl: any = [];

		if (filterSet && filterSet.length) {

			filterSet.forEach((group: FilterGroup) => {
				const groupParts: any[] = [];

				group.filters.forEach((filter: Filter) => {
					groupParts.push([filter.type, filter.field, filter.operator, filter.value]);
				});

				filterForUrl.push(groupParts);
			});
		}

		return JSON.stringify(filterForUrl);
	}

	getFacetOptions(filter: Filter, filterConfig: FilterConfig): Observable<BbFacetListOption[]> {
		const fieldName = filter.field;

		const filterQueries: string[] = [];

		this.filterSet.forEach((group: FilterGroup) => {
			group.filters.forEach((filterFromSet: Filter) => {
				if (filterFromSet.field !== filter.field) {
					if (typeof filterFromSet.value === 'string') {
						filterQueries.push(filterFromSet.field + ':"' + filterFromSet.value + '"');
					} else if (filterFromSet.value.length) {
						filterQueries.push(filterFromSet.field + ':("' + filterFromSet.value.join('"%20OR%20"') + '")');
					}
				}
			});
		});

		const facetQueries: string[] = [];

		if (filterConfig.options) {
			for (const opt in filterConfig.options) {
				if (filterConfig.options[opt]) {
					if (filterConfig.options[opt].query.startsWith('-')) {
						facetQueries.push('-' + filterConfig.property + ':' + filterConfig.options[opt].query.replace('-', ''));
					} else {
						facetQueries.push(filterConfig.property + ':' + filterConfig.options[opt].query);
					}
				}
			}
		}

		// remove current facet from filterset so we get all possible values for this field:
		// merge baseFilterSet
		let fs = JSON.parse(JSON.stringify((this.baseFilterSet ? [...this.filterSet, ...this.baseFilterSet] : this.filterSet)));

		fs.forEach((group: FilterGroup) => {
			group.filters.forEach((filterFromSet: Filter, i) => {
				if (filterFromSet.field === filter.field) {
					group.filters.splice(i, 1);
				}
			});
		});

		let params: HttpParams = new HttpParams()
			.set('facet', 'on')
			.set('limit', '0')
			.set('facetfield[]', filter.field);

		let queryValue = '';

		if (this.queryParams && this.queryParams.getAll.length > 0) {
			params = params.set('offset', this.queryParams.get('offset') ? this.queryParams.get('offset') : '0');

			queryValue = this.queryParams.get('q');
			if (queryValue) {
				fs = this.addQueryToFilterSet(queryValue, fs);
			}

			const limit = this.queryParams.get('limit');
			if (limit) {
				params = params.set('limit', this.queryParams.get('limit') ? this.queryParams.get('limit') : '0');
			}

			const sort: string = this.queryParams.get('sort');
			if (sort) {
				params = params.set('sort', this.queryParams.get('sort'));
			}
		}

		if (this.sharedContext) {
			params = params.set('context', 'shared');
		}

		if (fs) {
			params = params.set('filterset', JSON.stringify(fs)); // remove filterQueries?
		}

		// cached?
		const cacheKey = fieldName + ';' + JSON.stringify(filterQueries) + ';'
			+ JSON.stringify(facetQueries) + JSON.stringify(fs) + '; q: ' + queryValue;

		if (this.cache.has(cacheKey)) {
			return of(this.cache.get(cacheKey));
		}

		facetQueries.forEach(facetQuery => {
			params = params.append('facetquery[]', facetQuery);
		});
		return this.sapiService.getUrl('/' + filter.type.toLowerCase(), params).pipe(
			map((data: SapiFacetResponse) => {

				const facetOptions: BbFacetListOption[] = [];

				if (data && data.facets && data.facets[filter.field]) {
					let hasNo = false;
					let hasYes = false;
					let amount = 0;

					for (let i = 0; i < data.facets[filter.field].length; i += 2) {

						const value = data.facets[filter.field][i].toString();
						let alreadyUpdated = false;
						let label = value; // default

						if (filterConfig.pipes) {
							for (const pipe of filterConfig.pipes) {
								label = pipe.transform(label);
							}
						}

						if (filterConfig.type === 'boolean') {
							if (value === 'true') {
								label = this.translate.instant('YES');
								amount += parseInt(data.facets[filter.field][i + 1].toString(), 10);
								hasYes = true;
							} else {
								label = this.translate.instant('NO');
								amount += parseInt(data.facets[filter.field][i + 1].toString(), 10);
								hasNo = true;
							}
							facetOptions.forEach((facetListOption: BbFacetListOption) => {
								if (facetListOption.label === label) {
									facetListOption.amount += parseInt(data.facets[filter.field][i + 1].toString(), 10);
									alreadyUpdated = true;
								}
							});
						} else if (filterConfig.type === 'facetquery' && filterConfig.useFacetqueryResults) {
							alreadyUpdated = true;
						}

						if (!alreadyUpdated) {
							facetOptions.push({
								value,
								label,
								amount: parseInt(data.facets[filter.field][i + 1].toString(), 10)
							});
						}
					}

					if (filterConfig.type === 'boolean') {
						if (!hasYes) {
							facetOptions.push({
								value: 'true',
								label: this.translate.instant('YES'),
								amount: 0
							});
						}
						if (!hasNo) {
							facetOptions.push({
								value: 'false',
								label: this.translate.instant('NO'),
								amount: (data.numfound - amount)
							});
						}
					} else if (filterConfig.type === 'facetquery' && filterConfig.useFacetqueryResults) {
						for (const item in filterConfig.options) {
							if (filterConfig.options[item]) {

								let query = '';
								let filterQuery = '';
								if (filterConfig.options[item].query.startsWith('-')) {
									query = (filterConfig.options[item].query);
									filterQuery = ('-' + filterConfig.property + ':' + filterConfig.options[item].query.replace('-', ''));
								} else {
									query = (filterConfig.options[item].query);
									filterQuery = (filterConfig.property + ':' + filterConfig.options[item].query);
								}
								const facetqueryAmount = data.facet_queries[filterQuery];
								if (facetqueryAmount || facetqueryAmount === 0) {
									facetOptions.push({
										value: query,
										label: filterConfig.options[item].label,
										amount: facetqueryAmount
									});
								}
							}
						}
					}
				} else if (data.facet_queries) {

					for (const item in filterConfig.options) {
						if (filterConfig.options[item]) {
							const query = filterConfig.options[item].query;
							const amount = data.facet_queries[filterConfig.property + ':' + query];

							if (amount || amount === 0) {
								facetOptions.push({
									value: query,
									label: filterConfig.options[item].label,
									amount
								});
							}
						}

					}
				}

				this.cache.set(cacheKey, facetOptions);

				return facetOptions;
			}));
	}

	saveCustomPreset(title: string) {
		const customPreset = {
			title: this.translate.instant(title),
			filters: this.filterSet,
			usetype: 'preset',
			filterType: 'filters',
			listtype: 'dynamic',
		};
		this.overlayService.setLoading(true);
		this.sapiService.putUrl('/preset', customPreset).subscribe(
			(preset) => {
				this.savePresetObservable.next(preset);
				this.overlayService.createToast(this.translate.instant('ENTITY_SAVED', {
					entity: this.translate.instant('PRESET').toLowerCase(),
					id: preset.title
				}), 'save');
			},
			(err) => {
				this.overlayService.createToast(this.translate.instant('ERROR'), 'warning');
			}
		);
		this.overlayService.setLoading(false);
	}

	deleteCustomPreset(preset: BbFilterPreset) {
		const config = { entity: this.translate.instant('PRESET').toLowerCase(), id: preset.label };
		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETE_ENTITY', config),
			contents: this.translate.instant('DELETE_ENTITY_CONFIRM', config),
			onConfirm: () => {
				this.overlayService.setLoading(true);
				this.sapiService.delete('preset', preset.id).subscribe(
					(data) => {
						this.deletePresetObservable.next(preset.id);
						const msg = this.translate.instant('ENTITY_DELETED', config);
						this.overlayService.createToast(msg, 'info-circle');
					},
					(err) => {
						this.overlayService.createToast(this.translate.instant('ERROR'), 'warning');
					}
				);
				this.overlayService.setLoading(false);
			}
		};
		this.overlayService.createDialog(dialog);
	}
}
